// SPDX-License-Identifier: BUSL-1.1

/**
 * Organization switcher dropdown component.
 *
 * Allows users to switch between organizations they have access to.
 * Shows current org name with a dropdown of available orgs.
 *
 */

import { ChevronsUpDown, Building2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useOrg } from '@/contexts/org-context';

/**
 * Organization switcher dropdown.
 *
 * Renders in the app header and allows quick org switching.
 * Hidden when only one org is available.
 */
export function OrgSwitcher() {
  const { organizations, currentOrg, switchOrg, isLoading } = useOrg();

  if (isLoading || organizations.length <= 1) {
    return null;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={
          <Button
            variant="ghost"
            className="flex items-center gap-2 text-sm text-neutral-300 hover:bg-neutral-800/50"
          >
            <Building2 className="size-4 text-emerald-400" />
            <span className="font-medium">{currentOrg?.name ?? 'Select Org'}</span>
            <ChevronsUpDown className="size-3 text-neutral-500" />
          </Button>
        }
      />
      <DropdownMenuContent align="end" side="bottom" className="w-56">
        <div className="px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-neutral-500">
          Organizations
        </div>
        {organizations.map((org) => (
          <DropdownMenuItem
            key={org.id}
            onClick={() => switchOrg(org.id)}
            className={org.id === currentOrg?.id ? 'bg-neutral-800' : ''}
          >
            <div className="flex flex-col">
              <span className="font-medium">{org.name}</span>
              <span className="text-xs text-neutral-500">@{org.slug}</span>
            </div>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
